import { useEffect, useRef } from "react";
import { useRafMouseHandler } from "@/lib/useRafMouseHandler";
import "./CompanyV2ServiceCard.css";

function clampTilt(value, max = 8) {
  return Math.max(-max, Math.min(max, value));
}

export default function CompanyV2ServiceCard({ children, testId, className = "", flipped = false }) {
  const containerRef = useRef(null);
  const surfaceRef = useRef(null);

  const resetTilt = () => {
    const el = containerRef.current;
    if (!el) return;
    el.style.transition = "transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)";
    el.style.transform = "translateY(0) rotateX(0deg) rotateY(0deg)";
    if (surfaceRef.current) {
      surfaceRef.current.style.setProperty("--mouse-x", "50%");
      surfaceRef.current.style.setProperty("--mouse-y", "50%");
    }
  };

  const applyMove = (e) => {
    if (flipped) return;
    const el = containerRef.current;
    const surface = surfaceRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const angleX = clampTilt((cy - e.clientY) / 20);
    const angleY = clampTilt((e.clientX - cx) / 20);

    el.style.transition = "none";
    el.style.transform = `perspective(1200px) rotateX(${angleX}deg) rotateY(${angleY}deg) translateY(-4px)`;

    if (surface) {
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      surface.style.setProperty("--mouse-x", `${x}%`);
      surface.style.setProperty("--mouse-y", `${y}%`);
    }
  };

  const { handleMove, cancelPending } = useRafMouseHandler(applyMove, { enabled: !flipped });

  useEffect(() => {
    if (flipped) resetTilt();
  }, [flipped]);

  useEffect(() => {
    window.addEventListener("blur", resetTilt);
    return () => window.removeEventListener("blur", resetTilt);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`ds-company-v2-service-card${flipped ? " is-flipped" : ""} ${className}`.trim()}
      data-testid={testId}
      onMouseMove={handleMove}
      onMouseLeave={() => {
        cancelPending();
        resetTilt();
      }}
    >
      <div className="ds-company-v2-service-card__frame">
        <div className="ds-company-v2-service-card__neon" aria-hidden="true" />
        <div className="ds-company-v2-service-card__surface" ref={surfaceRef}>
          <div className="ds-company-v2-service-card__shine" aria-hidden="true" />
          <div className="ds-company-v2-service-card__content">{children}</div>
        </div>
      </div>
    </div>
  );
}
